import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

import { Accordion, AccordionSummary, AccordionDetails } from './FaqAccordion';
import { FAQForm } from './FaqManage';
import { DialogEditContext, Wrapper, WrapperContext } from '../layout';

export interface FAQProps {
  entity: 'event' | 'spot' | 'consumer';
  entityId: string;
  FAQs: Array<{
    id: string;
    idx: number;
    question: string;
    answer: string;
  }>;
}

function FAQ(props: FAQProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('FAQ must be used inside DialogEditWrapper');
  }

  const { isOwner, isEditMode } = context;
  const { FAQs } = props;
  const { t } = useTranslation('spot');

  if (!isEditMode || (!isOwner && !FAQs.length)) {
    return null;
  }

  return (
    <Wrapper type="faq">
      <Wrapper.Header
        title={t('title.FAQ')}
        isSingleValidate={true}
        openCtaLabel={!FAQs.length ? 'add' : 'manage'}
      />
      <Wrapper.Body sx={{ p: 0, backgroundColor: 'transparent' }}>
        <FAQinner {...props} />
      </Wrapper.Body>
    </Wrapper>
  );
}

function FAQinner(props: FAQProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('FAQinner must be used inside Wrapper');
  }

  const { isManaging, managingType } = context;

  return isManaging === managingType ? <FAQForm {...props} /> : <FAQdisplay {...props} />;
}

function FAQdisplay(props: FAQProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('FAQ display must be used inside Wrapper');
  }

  const { FAQs } = props;
  const [expanded, setExpanded] = React.useState<string | false>('');
  const { t } = useTranslation('event');
  const theme = useTheme();

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  if (FAQs.length === 0) {
    return (
      <Wrapper.Body>
        <Typography color="text.secondary" fontSize=".8rem" fontStyle="italic">
          {t('text.There are no FAQs yet, you can create one if needed.')}
        </Typography>
      </Wrapper.Body>
    );
  }

  return FAQs.map((faq, index, allFaqs) => {
    const isFirst = index === 0;
    const isLast = index === allFaqs.length - 1;

    const topCornerRadius = isFirst ? 1 : 0;
    const bottomCornerRadius = !isLast || (isLast && expanded === faq.id) ? 0 : 1;

    const borderRadius = theme.spacing(
      topCornerRadius,
      topCornerRadius,
      bottomCornerRadius,
      bottomCornerRadius
    );

    return (
      <Accordion key={faq.id} expanded={expanded === faq.id} onChange={handleChange(faq.id)}>
        <AccordionSummary
          aria-controls={`panel${index}d-content`}
          id={`panel${index}d-header`}
          sx={{ borderRadius }}
        >
          <Typography
            fontWeight="bold"
            color={`text.${expanded === faq.id ? 'primary' : 'secondary'}`}
            fontSize=".8rem"
            pr={1}
          >
            {faq.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            borderRadius: isLast && expanded === faq.id ? theme.spacing(0, 0, 1, 1) : 0,
          }}
        >
          <Typography
            color="text.primary"
            fontSize=".7rem"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }}
            sx={{ m: 0, p: 0, '& p': { m: 0, p: 0 } }}
          />
        </AccordionDetails>
      </Accordion>
    );
  });
}

export default React.memo(FAQ);
